import React, { createContext, useState, useContext, useEffect } from 'react';
import { firebaseService, auth } from '../services/firebase';

const AuthContext = createContext({
  currentUser: null,
  userProfile: null,
  userSettings: null,
  loading: true,
  error: null,
  login: async () => { },
  signup: async () => { },
  logout: async () => { },
  signInWithGoogle: async () => { },
  updateProfile: async () => { },
  updateUserRole: async () => { },
  resetPassword: async () => { },
  reloadUser: async () => { },
  updateUserEmail: async () => { }
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reloadUser = async () => {
    try {
      const fbUser = auth.currentUser;
      if (!fbUser) return null;

      await fbUser.reload();

      let userData = null;
      try {
        userData = await firebaseService.getUserData(fbUser.uid);
      } catch (err) {
        console.warn('Error fetching user data during reloadUser:', err);
      }

      const isVerified = Boolean(fbUser.emailVerified || userData?.emailVerified);
      const effectiveEmail = fbUser.email || userData?.email || '';
      const updatedUser = {
        uid: fbUser.uid,
        displayName: fbUser.displayName || userData?.displayName || userData?.name || '',
        photoURL: fbUser.photoURL || userData?.photoURL || '',
        providerData: fbUser.providerData,
        phoneNumber: fbUser.phoneNumber || userData?.phone || userData?.phoneNumber || '',
        ...(userData || {}),
        email: effectiveEmail,
        emailVerified: isVerified,
        reload: reloadUser,
        getIdToken: (...args) => fbUser.getIdToken(...args),
        getIdTokenResult: (...args) => fbUser.getIdTokenResult(...args),
      };

      setCurrentUser(updatedUser);
      if (userData) {
        setUserProfile(userData);
      }
      return updatedUser;
    } catch (err) {
      console.error('Error reloading user:', err);
      throw err;
    }
  };

  useEffect(() => {
    // Set loading to true at the start of the auth state change
    setLoading(true);

    const unsubscribe = firebaseService.onAuthStateChange(async (user) => {
      try {
        if (user) {
          let userData = null;
          try {
            userData = await firebaseService.getUserData(user.uid);
          } catch (error) {
            console.warn('Error fetching user data in onAuthStateChange:', error);
          }

          const isVerified = Boolean(user.emailVerified || userData?.emailVerified);
          const effectiveEmail = user.email || userData?.email || '';
          const userWithProfile = {
            uid: user.uid,
            displayName: user.displayName || userData?.displayName || userData?.name || '',
            photoURL: user.photoURL || userData?.photoURL || '',
            providerData: user.providerData,
            phoneNumber: user.phoneNumber || userData?.phone || userData?.phoneNumber || '',
            ...(userData || {}),
            email: effectiveEmail,
            emailVerified: isVerified,
            reload: reloadUser,
            getIdToken: (...args) => (user.getIdToken ? user.getIdToken(...args) : (auth.currentUser ? auth.currentUser.getIdToken(...args) : null)),
            getIdTokenResult: (...args) => (user.getIdTokenResult ? user.getIdTokenResult(...args) : (auth.currentUser ? auth.currentUser.getIdTokenResult(...args) : null)),
          };

          setCurrentUser(userWithProfile);
          if (userData) {
            setUserProfile(userData);
          }
        } else {
          setCurrentUser(null);
          setUserProfile(null);
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        setError(error.message);
        setCurrentUser(null);
        setUserProfile(null);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const userCredential = await firebaseService.login(email, password);
      return userCredential;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const signup = async (email, password, userData) => {
    try {
      setError(null);
      await firebaseService.signup(email, password, userData);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      setError(null);
      const userCredential = await firebaseService.googleSignIn();
      return userCredential;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await firebaseService.logout();
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const updateProfile = async (data) => {
    try {
      setError(null);
      if (!currentUser) throw new Error('No user logged in');

      await firebaseService.updateUserProfile(currentUser.uid, data);
      const updatedProfile = await firebaseService.getUserData(currentUser.uid);
      setUserProfile(updatedProfile);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      setError(null);
      if (!currentUser) throw new Error('No user logged in');

      await firebaseService.updateUserRole(userId, newRole);

      // If updating own role, refresh profile
      if (userId === currentUser.uid) {
        const profile = await firebaseService.getUserData(userId);
        setUserProfile(profile);
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const resetPassword = async (email) => {
    return firebaseService.resetPassword(email);
  };

  const updateUserPassword = async (password) => {
    return firebaseService.updateUserPassword(password);
  };

  const updateUserEmail = async (newEmail) => {
    try {
      setError(null);
      const cleanEmail = (newEmail || '').trim().toLowerCase();
      if (!cleanEmail) throw new Error('A valid email address is required.');

      const fbUser = auth.currentUser;
      if (!fbUser) throw new Error('No user currently logged in.');

      // Update in Firestore user profile
      await firebaseService.updateUserProfile(fbUser.uid, { email: cleanEmail });

      // Update in Firebase Auth or send verification email
      try {
        const { updateEmail, verifyBeforeUpdateEmail } = await import('firebase/auth');
        if (typeof verifyBeforeUpdateEmail === 'function') {
          await verifyBeforeUpdateEmail(fbUser, cleanEmail);
        } else if (typeof updateEmail === 'function') {
          await updateEmail(fbUser, cleanEmail);
        }
      } catch (authErr) {
        console.warn('Firebase Auth email update notice:', authErr);
      }

      await reloadUser();
      return { success: true };
    } catch (err) {
      console.error('Error updating user email:', err);
      setError(err.message);
      throw err;
    }
  };

  const value = {
    currentUser,
    userProfile,
    userSettings,
    loading,
    error,
    login,
    signup,
    logout,
    signInWithGoogle,
    updateProfile,
    updateUserRole,
    resetPassword,
    updateUserPassword,
    reloadUser,
    updateUserEmail
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
